import React from 'react'
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { formatDateTime, formatTradingDay } from './dailyDataUtils'

export default function ImportErrorsModal({
  open,
  onClose,
  title,
  tradingDay,
  runAt,
  errorCount = 0,
  imported,
  updated,
  skipped,
  filePath,
  errorDetails = [],
  loading = false,
}) {
  if (!open) return null

  const lines = Array.isArray(errorDetails) ? errorDetails.filter(Boolean) : []
  const subtitle = [
    tradingDay ? formatTradingDay(tradingDay) : null,
    runAt ? formatDateTime(runAt) : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true" aria-labelledby="import-errors-title">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b">
          <div className="min-w-0">
            <h2 id="import-errors-title" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0" />
              {title || 'Import errors'}
            </h2>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-700">
              <span><strong className="text-red-700">{errorCount}</strong> error{errorCount === 1 ? '' : 's'}</span>
              {imported != null && <span>{imported} imported</span>}
              {updated != null && updated > 0 && <span>{updated} synced</span>}
              {skipped != null && skipped > 0 && <span>{skipped} skipped</span>}
            </div>
            {filePath && (
              <p className="text-xs font-mono text-gray-500 mt-2 truncate" title={filePath}>{filePath}</p>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : lines.length === 0 ? (
            <p className="text-sm text-gray-600">
              No detailed error lines were stored for this run.
              {errorCount > 0 && ' Re-run the import after deploying the latest version to capture row-level messages.'}
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-3">
                Showing {lines.length} sample{lines.length === 1 ? '' : 's'}
                {errorCount > lines.length ? ` of ${errorCount} total errors` : ''}.
                Fix mapping or data issues, then re-import.
              </p>
              <ul className="space-y-2">
                {lines.map((line, i) => (
                  <li
                    key={i}
                    className="text-xs font-mono text-red-900 bg-red-50 border border-red-100 rounded px-3 py-2 whitespace-pre-wrap break-words"
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded-md text-sm hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
